import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import { ROUTES } from "@/app/constants";
import { AppLayout } from "@/shared/layout/AppLayout";
import { AuthLayout } from "@/shared/layout/AuthLayout";
import { NotFoundPage } from "@/shared/layout/NotFoundPage";
import { PendingApprovalPage } from "@/shared/layout/PendingApprovalPage";
import AuthGuard from "./AuthGuard";
import { GuestGuard } from "./GuestGuard";
import { RoleGuard } from "./RoleGuard";
import { RoleHomeRedirect } from "./RoleHomeRedirect";

const LoginPage = lazy(() =>
  import("@/features/auth").then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("@/features/auth").then((module) => ({ default: module.RegisterPage })),
);
const DashboardPage = lazy(() =>
  import("@/features/dashboard").then((module) => ({ default: module.DashboardPage })),
);
const PackagesPage = lazy(() =>
  import("@/features/packages").then((module) => ({ default: module.default })),
);
const PricingPage = lazy(() =>
  import("@/features/pricings").then((module) => ({ default: module.default })),
);
const ServicesPage = lazy(() =>
  import("@/features/services").then((module) => ({ default: module.default })),
);
const ItemsPage = lazy(() =>
  import("@/features/Items").then((module) => ({ default: module.default })),
);
const EventPage = lazy(() =>
  import("@/features/event-mode").then((module) => ({ default: module.default })),
);
const LeadHomePage = lazy(() =>
  import("@/features/lead_home").then((module) => ({ default: module.default })),
);
const VideoPage = lazy(() =>
  import("@/features/Videoassesment").then((module) => ({ default: module.default })),
);
const ApprovalPage = lazy(() =>
  import("@/features/approval").then((module) => ({ default: module.default })),
);
const LovPage = lazy(() =>
  import("@/features/LOV").then((module) => ({ default: module.default })),
);
const PackagePaymentPage = lazy(() =>
  import("@/features/packageApproval").then((module) => ({ default: module.default })),
);
const ClientPortalPage = lazy(() =>
  import("@/features/clientPortal").then((module) => ({ default: module.default })),
);
const ClientPortalReportPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalReportPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalProfilePage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalProfilePage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalCommunityPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalCommunityPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalBizCalendarPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalBizCalendarPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalRemediesPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalRemediesPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalDashaPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalDashaPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalPaymentPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalPaymentPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalLayout = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalLayout").then((module) => ({
    default: module.ClientPortalLayout,
  })),
);

/**
 * Route layout:
 *
 *   /                   → RoleHomeRedirect (role-aware landing)
 *   /login, /register   → GuestGuard shell (bounces logged-in users home)
 *   /event-mode, /video → GuestGuard shell (currently public marketing screens)
 *   /pending-approval   → AuthGuard only (any signed-in user; typically a
 *                          Pending Approval client lands here)
 *
 *   Admin shell         → AuthGuard → RoleGuard(ADMIN/SUPER_ADMIN/STAFF)
 *                          → AppLayout (sidebar/navbar), then:
 *     /dashboard, /services, /packages, /items, /pricing, /approval,
 *     /package-approval, /lov
 *
 *   Client portal       → AuthGuard → RoleGuard(CLIENT) → ClientPortalPage
 *     /portal
 *     /ld/              → gated behind AuthGuard (was fully public)
 */
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RoleHomeRedirect />,
  },

  // -------- Public / guest-only routes --------
  {
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: ROUTES.login, element: <LoginPage /> },
          { path: ROUTES.register, element: <RegisterPage /> },
          { path: ROUTES.event, element: <EventPage /> },
          { path: ROUTES.video, element: <VideoPage /> },
        ],
      },
    ],
  },

  // -------- Any authenticated user (regardless of role/status) --------
  {
    element: <AuthGuard />,
    children: [
      { path: ROUTES.pendingApproval, element: <PendingApprovalPage /> },

      // /ld/ (lead_home) is now behind AuthGuard — it holds the free
      // report + package-selection flow, which is only meaningful for
      // a signed-in lead/client.
      { path: ROUTES.lead_home, element: <LeadHomePage /> },
    ],
  },

  // -------- Admin shell (ADMIN / SUPER_ADMIN / STAFF only) --------
  {
    element: <AuthGuard />,
    children: [
      {
        element: <RoleGuard allowedRoles={["ADMIN", "SUPER_ADMIN", "STAFF"]} />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { path: ROUTES.dashboard, element: <DashboardPage /> },
              { path: ROUTES.packages, element: <PackagesPage /> },
              { path: ROUTES.Services, element: <ServicesPage /> },
              {
                // Extra ADMIN-only tightening for the admin-management surfaces
                // that shouldn't be visible even to STAFF.
                element: <RoleGuard allowedRoles={["ADMIN", "SUPER_ADMIN"]} />,
                children: [
                  { path: ROUTES.pricing, element: <PricingPage /> },
                  { path: ROUTES.items, element: <ItemsPage /> },
                  { path: ROUTES.approval, element: <ApprovalPage /> },
                  { path: ROUTES.packageApproval, element: <PackagePaymentPage /> },
                  { path: ROUTES.lov, element: <LovPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // -------- Client portal (CLIENT only) --------
  {
    element: <AuthGuard />,
    children: [
      {
        element: <RoleGuard allowedRoles={["CLIENT"]} />,
        children: [
          {
            element: <ClientPortalLayout />,
            children: [
              { path: ROUTES.portal, element: <ClientPortalPage /> },
              { path: ROUTES.portalReports, element: <ClientPortalReportPage /> },
              { path: ROUTES.portalProfile, element: <ClientPortalProfilePage /> },
              { path: ROUTES.portalCommunity, element: <ClientPortalCommunityPage /> },
              { path: ROUTES.portalBizcalendar, element: <ClientPortalBizCalendarPage /> },
              { path: ROUTES.portalRemedies, element: <ClientPortalRemediesPage /> },
              { path: ROUTES.portalDasha, element: <ClientPortalDashaPage /> },
              { path: ROUTES.portalPayments, element: <ClientPortalPaymentPage /> },
            ],
          },
        ],
      },
    ],
  },

  {
    path: "*",
    element: <NotFoundPage />,
  },
]);
